import { useAuth } from '@/auth/useAuth';
import { NtcResumenTable } from '@/components/asesorias/NtcResumenTable';
import { Input, LoadingContainer, Modal } from '@/components/ui';
import { IndicadoresGrid } from '@/components/ui/IndicadoresGrid';
import { useEmpresa } from '@/hooks/useEmpresas';
// Notificaciones de indicadores desactivadas temporalmente
// import { useIndicadoresByEmpresa } from '@/hooks/useIndicadores';
import { useNtcsByEmpresa } from '@/hooks/useNtcs';
import '@/pages/admin/admin.css';
import { Ntc, empresasService } from '@/services';
import { asesoriasService } from '@/services/asesorias.service';
import { dashboardService } from '@/services/dashboard.service';
import { getAsesoriaEstadoBadgeVariant, getAsesoriaEstadoLabel, isAsesoriaActive, normalizeAsesoriaEstado } from '@/utils/asesoriaStatus';
import { formatHorasDecimales, horasDeAudios } from '@/utils/formatDuration';
import { renderRecomendacionesCompacto } from '@/utils/renderWithBold';
import { Pencil } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

type AsesoriaItem = {
  asesoriaId: number;
  estado: string;
  // Preaprobación transparente — ver normalizeAsesoriaEstado en utils/asesoriaStatus.ts.
  autoAprobar?: boolean;
  objetivo: string;
  fechaInicio: string;
  fechaFin: string | null;
  horas: number;
  orden: number | null;
  rechazos?: number;
  recomendaciones?: string | null;
  cumplimientoGeneral?: number | null;
  compromisos?: string | null;
  conclusiones?: string | null;
  actaLink?: string | null;
  asesor?: {
    asesorId: number;
    nombre: string;
  } | null;
  // Mientras la asesoría está EN_CURSO, `horas` sigue en 0 hasta que el asesor la envía a
  // revisión — se usa la duración de los audios como estimado en vivo (ver formatDuration.ts).
  audios?: Array<{ duracionSeg?: number }>;
};

type Tab = 'asesorias' | 'auditoria' | 'normas' | 'indicadores';

const TABS: { id: Tab; label: string }[] = [
  { id: 'asesorias', label: 'Asesorías' },
  { id: 'normas', label: 'Normas' },
  { id: 'indicadores', label: 'Indicadores' },
];

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return 'No registrada';
  try {
    return new Date(dateStr).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return 'No registrada';
  }
}

function formatDateTime(dateStr?: string): string {
  if (!dateStr) return 'No registrada';
  try {
    return new Date(dateStr).toLocaleString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'No registrada';
  }
}

function getCumplimientoClass(v: number): string {
  if (v >= 80) return 'text-[#2d7a4f] bg-[#eaf5ee]';
  if (v >= 50) return 'text-[#b56b00] bg-[#fdf3e0]';
  return 'text-[#c0392b] bg-[#fdf0ee]';
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

function chipColorClass(variant: string): string {
  if (variant === 'green') return 'bg-[#eaf5ee] text-[#2d7a4f]';
  if (variant === 'red') return 'bg-[#fdf0ee] text-[#c0392b]';
  if (variant === 'amber' || variant === 'yellow') return 'bg-[#fdf3e0] text-[#b56b00]';
  if (variant === 'blue') return 'bg-[#eaf1fb] text-[#1e5fa8]';
  if (variant === 'purple') return 'bg-[#f0edfc] text-[#5a38c4]';
  return 'bg-[#f2f1ee] text-[#666]';
}

function getActionLabel(estado: string): string | null {
  if (estado === 'FINALIZADA') return 'Ver detalle';
  if (estado === 'REVERTIDA') return 'Continuar asesoría';
  if (estado === 'EN_REVISION' || estado === 'SIN_EVALUAR') return 'Ver asesoría';
  if (estado === 'REQUIERE_CORRECCIONES') return 'Ver correcciones';
  return 'Continuar asesoría';
}

function getActionPath(nit: string, asesoriaId: number): string {
  return `/empresa/${nit}/asesoria/${asesoriaId}`;
}

// ── Tab: Asesorías activas ────────────────────────────────────
type EvidenciaItem = { evidenciaId: number; nombre: string; url: string; fechaAdicion: string };

interface AsesoriasPanelProps {
  asesorias: AsesoriaItem[];
  nit: string;
  focoId: number | null;
  settingFoco: boolean;
  puedeCrear: boolean;
  onSetFoco: (id: number) => void;
  onSelect: (id: number) => void;
  selectedId: number | null;
  evidencias: EvidenciaItem[];
  loadingEvidencias: boolean;
  resumenNtc: Array<{
    ntcId: number;
    nombre: string;
    total: number;
    antes: { C: number; NC: number; CP: number; NA: number; pct: number };
    despues: { C: number; NC: number; CP: number; NA: number; pct: number };
  }>;
}

function AsesoriasPanel({
  asesorias,
  nit,
  focoId,
  settingFoco,
  puedeCrear,
  onSetFoco,
  onSelect,
  selectedId,
  evidencias,
  loadingEvidencias,
  resumenNtc,
}: AsesoriasPanelProps) {
  const [showFocoInfo, setShowFocoInfo] = useState(false);
  const selected = asesorias.find((a) => a.asesoriaId === selectedId) ?? asesorias[0] ?? null;
  const { user } = useAuth();
  const currentAsesorId = user?.asesor?.id ? Number(user.asesor.id) : null;
  const showSelectedAsesor =
    Boolean(selected?.asesor?.nombre) &&
    (currentAsesorId === null || (selected?.asesor?.asesorId && Number(selected.asesor.asesorId) !== currentAsesorId));

  if (asesorias.length === 0) {
    return (
      <Link to={`/empresa/${nit}/nueva-asesoria`}>
        <button className="admin-btn primary w-full justify-center">Crear asesoría</button>
      </Link>
    );
  }

  return (
    <div className="flex gap-3 items-start">
      {/* Sidebar */}
      <div
        className="flex flex-col shrink-0"
        style={{ width: 200, background: 'white', border: '1px solid var(--admin-border)', borderRadius: 12, overflow: 'hidden' }}
      >
        <div className="px-3 py-2 border-b" style={{ borderColor: 'var(--admin-border)', background: 'var(--admin-surface)' }}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider animate-pulse" style={{ color: 'var(--admin-text3)' }}>
              Todas las asesorias
            </span>
            <button type="button" onClick={() => setShowFocoInfo(true)} className="text-[9px] text-[#0066cc] font-semibold hover:underline">
              ¿Que es el foco?
            </button>
          </div>
        </div>

        <Modal isOpen={showFocoInfo} onClose={() => setShowFocoInfo(false)} title="¿Que es el foco?" size="md">
          <div className="space-y-4">
            <p className="text-[13px] leading-relaxed" style={{ color: 'var(--admin-text2)' }}>
              <strong>Foco Activo:</strong> Indica cual de las asesorias recibe de forma automatica las subidas de audio, evaluaciones e
              indicadores en este momento. Solo puede haber una asesoria en foco a la vez.
            </p>
            <div className="flex justify-end">
              <button type="button" onClick={() => setShowFocoInfo(false)} className="admin-btn primary">
                Entendido
              </button>
            </div>
          </div>
        </Modal>

        {asesorias.map((a) => {
          const estadoNorm = normalizeAsesoriaEstado({ estado: a.estado, autoAprobar: a.autoAprobar });
          const estadoLabel = getAsesoriaEstadoLabel(estadoNorm);
          const estadoVariant = getAsesoriaEstadoBadgeVariant(estadoNorm);
          const isSelected = a.asesoriaId === selectedId;
          const isFoco = a.asesoriaId === focoId;
          const isActive = isAsesoriaActive(estadoNorm);

          return (
            <div
              key={a.asesoriaId}
              role="button"
              tabIndex={0}
              onClick={(e) => {
                // Ctrl/Cmd+clic abre el acta en una pestaña nueva en vez de seleccionar la
                // asesoría — solo si esta asesoría tiene acta generada.
                if ((e.ctrlKey || e.metaKey) && a.actaLink) {
                  e.preventDefault();
                  window.open(a.actaLink, '_blank', 'noopener,noreferrer');
                  return;
                }
                onSelect(a.asesoriaId);
              }}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' ? onSelect(a.asesoriaId) : undefined)}
              className="w-full text-left px-3 py-2.5 transition-colors cursor-pointer"
              title={a.actaLink ? 'Ctrl+clic (Cmd+clic en Mac) para abrir el acta' : undefined}
              style={{
                background: isSelected ? 'var(--admin-blue-bg)' : 'transparent',
                borderLeft: isSelected ? '3px solid var(--admin-blue)' : '3px solid transparent',
                opacity: isActive ? 1 : 0.6,
              }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-semibold" style={{ color: isSelected ? 'var(--admin-blue)' : 'var(--admin-text)' }}>
                  {a.orden === 1 ? 'Diagnóstico' : a.orden != null ? `#${a.orden - 1}` : `#${a.asesoriaId}`}
                </span>
                <span className={`admin-chip text-[9px] ${chipColorClass(estadoVariant)}`} style={{ padding: '1px 5px' }}>
                  {estadoLabel}
                </span>
              </div>
              {a.asesor?.nombre && (currentAsesorId === null || (a.asesor.asesorId && Number(a.asesor.asesorId) !== currentAsesorId)) && (
                <p className="text-[9px] font-medium truncate mb-0.5 text-blue-600">{a.asesor.nombre}</p>
              )}
              <p className="text-[10px] leading-tight line-clamp-2" style={{ color: 'var(--admin-text3)' }}>
                {a.objetivo}
              </p>
              {isFoco ? (
                <div
                  className="inline-flex items-center gap-1.5 mt-2 px-2 py-1 rounded-lg text-[10px] font-semibold w-full justify-center"
                  style={{ background: 'var(--admin-green-bg)', color: 'var(--admin-green)', border: '1px solid var(--admin-green)' }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  </svg>
                  En foco · recibe audios
                </div>
              ) : estadoNorm === 'EN_CURSO' || estadoNorm === 'REQUIERE_CORRECCIONES' || estadoNorm === 'REVERTIDA' ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetFoco(a.asesoriaId);
                  }}
                  disabled={settingFoco}
                  className="inline-flex items-center gap-1.5 mt-2 px-2 py-1 rounded-lg text-[10px] font-semibold w-full justify-center transition-colors"
                  style={{ background: 'var(--admin-surface2)', color: 'var(--admin-text2)', border: '1px solid var(--admin-border)' }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  </svg>
                  {settingFoco ? 'Actualizando…' : 'Establecer foco'}
                </button>
              ) : null}
            </div>
          );
        })}

        {puedeCrear && (
          <div className="pt-2 px-3 pb-3">
            <Link to={`/empresa/${nit}/nueva-asesoria`}>
              <button className="admin-btn w-full justify-center text-[11px]">+ Nueva asesoría</button>
            </Link>
            <p className="text-[10px] text-[#999] text-center mt-1">Envía la asesoría en curso a revisión para crear una nueva.</p>
          </div>
        )}
      </div>

      {/* Detalle de la asesoría seleccionada */}
      {selected && (
        <div className="flex-1 min-w-0 admin-card">
          <div className="admin-card-header flex items-center justify-between">
            <div>
              <span className="text-[13px] font-semibold text-[#1a1a1a]">
                {selected.orden === 1
                  ? 'Diagnóstico'
                  : selected.orden != null
                    ? `Asesoría #${selected.orden - 1}`
                    : `Asesoría #${selected.asesoriaId}`}
              </span>
            </div>
            <span
              className={`admin-chip ${chipColorClass(getAsesoriaEstadoBadgeVariant(normalizeAsesoriaEstado({ estado: selected.estado, autoAprobar: selected.autoAprobar })))}`}
            >
              {getAsesoriaEstadoLabel(normalizeAsesoriaEstado({ estado: selected.estado, autoAprobar: selected.autoAprobar }))}
            </span>
          </div>
          <div className="p-4">
            <div
              className="admin-detail-grid mb-4"
              style={{ gridTemplateColumns: showSelectedAsesor ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)' }}
            >
              {showSelectedAsesor && (
                <div className="admin-detail-field">
                  <div className="text-[10px] text-[#999] uppercase tracking-wider font-medium mb-1">Asesor</div>
                  <div className="text-[13px] font-semibold text-[#1e5fa8] truncate">{selected.asesor?.nombre}</div>
                </div>
              )}
              <div className="admin-detail-field">
                <div className="text-[10px] text-[#999] uppercase tracking-wider font-medium mb-1">Inicio</div>
                <div className="text-[13px] text-[#1a1a1a]">{formatDateTime(selected.fechaInicio)}</div>
              </div>
              <div className="admin-detail-field">
                <div className="text-[10px] text-[#999] uppercase tracking-wider font-medium mb-1">Horas</div>
                <div className="text-[13px] text-[#1a1a1a]">
                  {formatHorasDecimales(Number(selected.horas) || horasDeAudios(selected.audios))}
                </div>
              </div>
            </div>

            {(loadingEvidencias || resumenNtc.length > 0) && (
              <div className="my-4">
                <div className="text-[10px] text-[#999] uppercase tracking-wider font-medium mb-2">Evaluación por norma</div>
                <NtcResumenTable rows={resumenNtc} loading={loadingEvidencias} />
              </div>
            )}
            <div className="mb-4">
              <div className="text-[10px] text-[#999] uppercase tracking-wider font-medium mb-1">Objetivo</div>
              <p className="text-[13px] text-[#1a1a1a] leading-relaxed">{selected.objetivo}</p>
            </div>
            {selected.compromisos && (
              <div className="mb-3">
                <div className="text-[10px] text-[#999] uppercase tracking-wider font-medium mb-1">Compromisos</div>
                <p className="text-[12px] text-[#1a1a1a] leading-relaxed">{selected.compromisos}</p>
              </div>
            )}
            {selected.conclusiones && (
              <div className="mb-4">
                <div className="text-[10px] text-[#999] uppercase tracking-wider font-medium mb-1">Conclusiones</div>
                <p className="text-[12px] text-[#1a1a1a] leading-relaxed">{selected.conclusiones}</p>
              </div>
            )}
            {selected.recomendaciones && (
              <div className="mb-4">
                <div className="text-[10px] text-[#999] uppercase tracking-wider font-medium mb-1">Motivos de revisión</div>
                {renderRecomendacionesCompacto(selected.recomendaciones)}
              </div>
            )}
            {(() => {
              const estadoNorm = normalizeAsesoriaEstado({ estado: selected.estado, autoAprobar: selected.autoAprobar });
              const label = getActionLabel(estadoNorm);
              if (!label) return null;
              return (
                <Link to={getActionPath(nit, selected.asesoriaId)}>
                  <button className="admin-btn primary w-full justify-center">{label}</button>
                </Link>
              );
            })()}

            {/* Evidencias */}
            <div className="mt-4">
              <div className="text-[10px] text-[#999] uppercase tracking-wider font-medium mb-2">Evidencias</div>
              {loadingEvidencias ? (
                <div className="text-[12px] text-[#999]">Cargando...</div>
              ) : evidencias.length === 0 ? (
                <div className="text-[12px] text-[#999]">Sin evidencias cargadas</div>
              ) : (
                <ul className="flex flex-col gap-1">
                  {evidencias.map((ev) => (
                    <li key={ev.evidenciaId} className="flex items-center gap-1.5 text-[12px]">
                      <a
                        href={ev.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="truncate max-w-55 underline"
                        style={{ color: 'var(--admin-blue)' }}
                        title={ev.nombre}
                      >
                        {ev.nombre}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab: Normas ───────────────────────────────────────────────
function calcCumplimiento(c: number, cp: number, nc: number): number | null {
  const denom = c + cp + nc;
  return denom > 0 ? ((c + cp * 0.5) / denom) * 100 : null;
}

function NtcTable({ ntcs, nit }: { ntcs: Ntc[] | null; nit: string }) {
  return (
    <div className="admin-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="admin-table w-full">
          <thead>
            <tr>
              <th>NTC</th>
              <th className="text-center">Total</th>
              <th className="text-center">C</th>
              <th className="text-center">NC</th>
              <th className="text-center">CP</th>
              <th className="text-center">NA</th>
              <th className="text-center">% Cumplimiento</th>
              <th className="text-center">Acción</th>
            </tr>
          </thead>
          <tbody>
            {ntcs
              ?.sort((o1, o2) => o1.orden - o2.orden)
              ?.map((ntc) => {
                const pct = calcCumplimiento(ntc.totalC, ntc.totalCP, ntc.totalNC);
                return (
                  <tr key={ntc.id}>
                    <td className="font-medium text-[#1a1a1a]">{ntc.nombre || ntc.id}</td>
                    <td className="text-center text-[#666]">{ntc.totalRequisitos}</td>
                    <td className="text-center text-[#666]">{ntc.totalC}</td>
                    <td className="text-center text-[#666]">{ntc.totalNC}</td>
                    <td className="text-center text-[#666]">{ntc.totalCP}</td>
                    <td className="text-center text-[#666]">{ntc.totalNA}</td>
                    <td className="text-center">
                      {pct !== null ? (
                        <span className={`admin-chip font-bold ${getCumplimientoClass(pct)}`}>
                          {pct.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
                        </span>
                      ) : (
                        <span style={{ color: 'var(--admin-text3)' }}>—</span>
                      )}
                    </td>
                    <td className="text-center">
                      <Link to={`/empresa/${nit}/ntc/${ntc.id}`}>
                        <button className="admin-btn text-[11px]">Ver detalle</button>
                      </Link>
                    </td>
                  </tr>
                );
              })}
            {ntcs &&
              ntcs.length > 0 &&
              (() => {
                const totals = ntcs.reduce(
                  (acc, n) => ({
                    total: acc.total + n.totalRequisitos,
                    C: acc.C + n.totalC,
                    NC: acc.NC + n.totalNC,
                    CP: acc.CP + n.totalCP,
                    NA: acc.NA + n.totalNA,
                  }),
                  { total: 0, C: 0, NC: 0, CP: 0, NA: 0 }
                );
                const pcts = ntcs.map((n) => calcCumplimiento(n.totalC, n.totalCP, n.totalNC)).filter((p): p is number => p !== null);
                const avgPct = pcts.length > 0 ? pcts.reduce((s, p) => s + p, 0) / pcts.length : null;
                return (
                  <tr style={{ background: 'var(--admin-surface2)', fontWeight: 600, borderTop: '2px solid var(--admin-border)' }}>
                    <td style={{ color: 'var(--admin-text)' }}>Total general</td>
                    <td className="text-center" style={{ color: 'var(--admin-text)' }}>
                      {totals.total}
                    </td>
                    <td className="text-center" style={{ color: 'var(--admin-text)' }}>
                      {totals.C}
                    </td>
                    <td className="text-center" style={{ color: 'var(--admin-text)' }}>
                      {totals.NC}
                    </td>
                    <td className="text-center" style={{ color: 'var(--admin-text)' }}>
                      {totals.CP}
                    </td>
                    <td className="text-center" style={{ color: 'var(--admin-text2)' }}>
                      {totals.NA}
                    </td>
                    <td className="text-center">
                      {avgPct !== null ? (
                        <span className={`admin-chip font-bold ${getCumplimientoClass(avgPct)}`}>
                          {avgPct.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
                        </span>
                      ) : (
                        <span style={{ color: 'var(--admin-text3)' }}>—</span>
                      )}
                    </td>
                    <td />
                  </tr>
                );
              })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export function EmpresaDetallePage() {
  const { nit, tab } = useParams<{ nit: string; tab?: string }>();
  const navigate = useNavigate();
  const { empresa, isLoading, error, refetch: refetchEmpresa } = useEmpresa(nit ?? '');
  const { data: ntcs } = useNtcsByEmpresa(nit ?? '');

  // Notificaciones de indicadores desactivadas temporalmente.
  // const { data: indicadores } = useIndicadoresByEmpresa(nit ?? '');
  // const warningIndicatorsCount = useMemo(() => {
  //   if (!indicadores) return 0;
  //   const limitDaysMap: Record<string, number> = {
  //     MENSUAL: 30,
  //     TRIMESTRAL: 90,
  //     SEMESTRAL: 180,
  //     ANUAL: 365,
  //   };
  //   return indicadores.filter((ind: any) => {
  //     if (!ind.ultimaEvaluacion) {
  //       return true; // Never evaluated count as warning
  //     }
  //     const date = new Date(ind.ultimaEvaluacion);
  //     const diffTime = Date.now() - date.getTime();
  //     const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  //     const limit = limitDaysMap[ind.frecuencia] || 30;
  //     const remaining = limit - diffDays;
  //     return remaining <= 7;
  //   }).length;
  // }, [indicadores]);

  const activeTab = useMemo<Tab>(() => {
    if (tab && TABS.some((t) => t.id === tab)) {
      return tab as Tab;
    }
    return 'asesorias';
  }, [tab]);
  const [asesorias, setAsesorias] = useState<AsesoriaItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [settingFoco, setSettingFoco] = useState(false);
  const [selectedEvidencias, setSelectedEvidencias] = useState<
    Array<{ evidenciaId: number; nombre: string; url: string; fechaAdicion: string }>
  >([]);
  const [selectedResumenNtc, setSelectedResumenNtc] = useState<
    Array<{
      ntcId: number;
      nombre: string;
      total: number;
      antes: { C: number; NC: number; CP: number; NA: number; pct: number };
      despues: { C: number; NC: number; CP: number; NA: number; pct: number };
    }>
  >([]);
  const [loadingEvidencias, setLoadingEvidencias] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [saveDetailsError, setSaveDetailsError] = useState<string | null>(null);
  const [asesoriasDesincronizadas, setAsesoriasDesincronizadas] = useState<
    Array<{ asesoriaId: number; estado: string; asesorId: number; objetivo: string }>
  >([]);
  const [sincronizando, setSincronizando] = useState(false);
  const [formValues, setFormValues] = useState({
    departamento: '',
    municipio: '',
    ubicacion: '',
    correoEmpresa: '',
    representanteLegal: '',
    rnt: '',
    rntCategoria: '',
    rntSubCategoria: '',
  });

  const openEditModal = () => {
    if (empresa) {
      setFormValues({
        departamento: empresa.departamento || '',
        municipio: empresa.municipio || '',
        ubicacion: empresa.ubicacion || '',
        correoEmpresa: empresa.correoEmpresa || '',
        representanteLegal: empresa.representanteLegal || '',
        rnt: empresa.rnt || '',
        rntCategoria: empresa.rntCategoria || '',
        rntSubCategoria: empresa.rntSubCategoria || '',
      });
    }
    setSaveDetailsError(null);
    setIsEditModalOpen(true);
  };

  const handleSaveDetails = async () => {
    if (!nit) return;
    setIsSavingDetails(true);
    setSaveDetailsError(null);
    try {
      const result = await empresasService.actualizarDetalleEmpresa(nit, {
        departamento: formValues.departamento.trim() || undefined,
        municipio: formValues.municipio.trim() || undefined,
        ubicacion: formValues.ubicacion.trim() || null,
        correoEmpresa: formValues.correoEmpresa.trim() || undefined,
        representanteLegal: formValues.representanteLegal.trim() || null,
        rnt: formValues.rnt.trim() || null,
        rntCategoria: formValues.rntCategoria.trim() || null,
        rntSubCategoria: formValues.rntSubCategoria.trim() || null,
      } as any);
      if (result.success) {
        await refetchEmpresa();
        setIsEditModalOpen(false);
      } else {
        setSaveDetailsError(result.message || 'Error al guardar los detalles');
      }
    } catch (err) {
      setSaveDetailsError(err instanceof Error ? err.message : 'Error al guardar los detalles');
    } finally {
      setIsSavingDetails(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'asesorias' || asesorias.length === 0) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') return;
      e.preventDefault();
      setSelectedId((prev) => {
        const idx = asesorias.findIndex((a) => a.asesoriaId === prev);
        const next = e.key === 'ArrowDown' ? Math.min(idx + 1, asesorias.length - 1) : Math.max(idx - 1, 0);
        return asesorias[next].asesoriaId;
      });
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [asesorias, activeTab]);

  const cargarAsesorias = () => {
    if (!nit) return;
    dashboardService
      .getHistorialAsesorias(nit)
      .then((res) => {
        if (res.success) {
          setAsesorias(res.asesorias);
          if (res.asesorias.length > 0) setSelectedId((prev) => prev ?? res.asesorias[0].asesoriaId);
        }
      })
      .catch(() => {});
  };

  useEffect(cargarAsesorias, [nit]);

  const cargarAsesoriasDesincronizadas = () => {
    if (!nit) return;
    empresasService
      .obtenerAsesoriasDesincronizadas(nit)
      .then((res) => {
        if (res.success) setAsesoriasDesincronizadas(res.desincronizadas);
      })
      .catch(() => {});
  };

  // Asesorías de un asesor anterior que quedaron sin sincronizar (no pasaron a SIN_EVALUAR al
  // reasignar) — habilita el botón "Sincronizar estados" solo si hay algo que resolver.
  useEffect(cargarAsesoriasDesincronizadas, [nit]);

  const handleSincronizarAsesorias = async () => {
    if (!nit) return;
    setSincronizando(true);
    try {
      await empresasService.sincronizarAsesorias(nit);
      cargarAsesoriasDesincronizadas();
      cargarAsesorias();
    } finally {
      setSincronizando(false);
    }
  };

  useEffect(() => {
    if (empresa?.asesoriaActivaId) setSelectedId((prev) => prev ?? Number(empresa.asesoriaActivaId));
  }, [empresa]);

  useEffect(() => {
    if (!selectedId || !nit) return;
    setLoadingEvidencias(true);
    setSelectedResumenNtc([]);
    asesoriasService
      .getById(selectedId, nit)
      .then((res) => {
        setSelectedEvidencias(
          res.asesoria.evidencias.map((e) => ({
            evidenciaId: e.evidenciaId,
            nombre: e.nombre,
            url: e.url,
            fechaAdicion: typeof e.fechaAdicion === 'string' ? e.fechaAdicion : new Date(e.fechaAdicion).toISOString(),
          }))
        );
        setSelectedResumenNtc(res.asesoria.resumenPorNtc ?? []);
      })
      .catch(() => {
        setSelectedEvidencias([]);
        setSelectedResumenNtc([]);
      })
      .finally(() => setLoadingEvidencias(false));
  }, [selectedId, nit]);

  const handleSetFoco = async (asesoriaId: number) => {
    if (!nit || settingFoco) return;
    setSettingFoco(true);
    try {
      await asesoriasService.setEnFoco(nit, asesoriaId);
      await refetchEmpresa();
    } finally {
      setSettingFoco(false);
    }
  };

  if (isLoading) return <LoadingContainer text="Cargando empresa..." />;

  if (error || !empresa) {
    return (
      <div className="text-center py-12">
        <h2 className="text-[13px] font-semibold text-[#1a1a1a]">{error || 'Empresa no encontrada'}</h2>
        <Link to="/">
          <button className="admin-btn primary mt-4">Volver al inicio</button>
        </Link>
      </div>
    );
  }

  const estadoActivoEmpresa = empresa.asesoriaActivaEstado ? normalizeAsesoriaEstado({ estado: empresa.asesoriaActivaEstado }) : null;
  const estadoActivoText = estadoActivoEmpresa ? getAsesoriaEstadoLabel(estadoActivoEmpresa) : 'Sin asesoría activa';
  const estadoActivoVariant = estadoActivoEmpresa ? getAsesoriaEstadoBadgeVariant(estadoActivoEmpresa) : 'gray';
  const puedeCrear = asesorias.every((a) => normalizeAsesoriaEstado({ estado: a.estado, autoAprobar: a.autoAprobar }) !== 'EN_CURSO');
  const focoId = empresa.asesoriaActivaId ? Number(empresa.asesoriaActivaId) : null;

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Volver */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-[12px] text-[#999] hover:text-[#1a1a1a] mb-4 transition-colors"
        >
          ← Volver
        </button>

        {asesoriasDesincronizadas.length > 0 && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-purple-200 bg-purple-50 px-4 py-3">
            <p className="text-[12px] text-purple-900">
              Hay {asesoriasDesincronizadas.length} asesoría(s) de un asesor anterior que no quedaron sincronizadas (deberían estar "Sin
              evaluar").
            </p>
            <button
              type="button"
              onClick={handleSincronizarAsesorias}
              disabled={sincronizando}
              className="shrink-0 rounded-lg bg-purple-600 px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-purple-700 disabled:opacity-50"
            >
              {sincronizando ? 'Sincronizando...' : 'Sincronizar estados'}
            </button>
          </div>
        )}

        {/* Header empresa */}
        <div className="admin-detail-panel mb-4">
          <div className="flex items-center gap-3">
            <div className="admin-avatar" style={{ background: '#eaf5ee', color: '#2d7a4f' }}>
              {getInitials(empresa.nombre)}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-[15px] font-semibold text-[#1a1a1a] leading-tight truncate">{empresa.nombre}</h1>
              <p className="text-[12px] text-[#999]">NIT: {empresa.nit}</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t flex items-start justify-between gap-4" style={{ borderColor: 'var(--admin-border)' }}>
            <div className="flex-1 flex flex-wrap gap-x-4 gap-y-1.5">
              {!(empresa.departamento || empresa.municipio || empresa.correoEmpresa || empresa.representanteLegal || empresa.rnt) && (
                <span className="text-[11px] text-[#999] italic">Sin datos de contacto o detalles registrados.</span>
              )}
              {empresa.municipio && (
                <span className="text-[11px] text-(--admin-text3)">
                  <span className="font-medium text-(--admin-text2)">Municipio:</span> {empresa.municipio}
                  {empresa.departamento ? `, ${empresa.departamento}` : ''}
                </span>
              )}
              {empresa.ubicacion && (
                <span className="text-[11px] text-(--admin-text3)">
                  <span className="font-medium text-(--admin-text2)">Ubicación:</span> {empresa.ubicacion}
                </span>
              )}
              {empresa.correoEmpresa && (
                <span className="text-[11px] text-(--admin-text3)">
                  <span className="font-medium text-(--admin-text2)">Correo:</span> {empresa.correoEmpresa}
                </span>
              )}
              {empresa.representanteLegal && (
                <span className="text-[11px] text-(--admin-text3)">
                  <span className="font-medium text-(--admin-text2)">Representante:</span> {empresa.representanteLegal}
                </span>
              )}
              {empresa.rnt && (
                <span className="text-[11px] text-(--admin-text3)">
                  <span className="font-medium text-(--admin-text2)">RNT:</span> {empresa.rnt}
                  {empresa.rntCategoria && ` · ${empresa.rntCategoria}`}
                  {empresa.rntSubCategoria && ` / ${empresa.rntSubCategoria}`}
                </span>
              )}
            </div>
            <button
              onClick={openEditModal}
              className="opacity-30 hover:opacity-100 transition-opacity p-1 cursor-pointer shrink-0"
              title="Editar detalles"
            >
              <Pencil className="w-3.5 h-3.5 text-[#1a1a1a]" />
            </button>
          </div>
        </div>

        {/* Tabs — desktop: barra horizontal, mobile: select dropdown */}
        <div className="mb-4">
          {/* Mobile select */}
          <div className="sm:hidden mb-3">
            <select
              value={activeTab}
              onChange={(e) => navigate(`/empresa/${nit}/${e.target.value}`)}
              className="w-full text-[13px] font-medium rounded-xl px-3 py-2.5"
              style={{
                background: 'white',
                border: '1px solid var(--admin-border)',
                color: 'var(--admin-text)',
                appearance: 'auto',
              }}
            >
              {TABS.map((t) => {
                // Notificaciones de indicadores desactivadas temporalmente
                // const badgeText = t.id === 'indicadores' && warningIndicatorsCount > 0 ? ` (${warningIndicatorsCount} por vencer)` : '';
                return (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Desktop tab bar */}
          <div className="hidden sm:block admin-tab-bar" style={{ marginBottom: 0 }}>
            {TABS.map((t) => {
              return (
                <button
                  key={t.id}
                  className={`admin-tab ${activeTab === t.id ? 'active' : ''}`}
                  onClick={() => navigate(`/empresa/${nit}/${t.id}`)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  {t.label}
                  {/* Notificaciones de indicadores desactivadas temporalmente
                  {t.id === 'indicadores' && warningIndicatorsCount > 0 && (
                    <span
                      className="admin-badge"
                      style={{ marginLeft: 0, padding: '1px 5px', fontSize: '9px', minWidth: '15px', textAlign: 'center' }}
                    >
                      {warningIndicatorsCount}
                    </span>
                  )} */}
                </button>
              );
            })}
          </div>
          <div className="sm:hidden" style={{ borderBottom: '1px solid var(--admin-border)', marginBottom: 16 }} />
        </div>

        {/* Tab content */}
        <div>
          {activeTab === 'asesorias' && (
            <AsesoriasPanel
              asesorias={asesorias}
              nit={empresa.nit}
              focoId={focoId}
              settingFoco={settingFoco}
              puedeCrear={puedeCrear}
              onSetFoco={handleSetFoco}
              onSelect={setSelectedId}
              selectedId={selectedId}
              evidencias={selectedEvidencias}
              loadingEvidencias={loadingEvidencias}
              resumenNtc={selectedResumenNtc}
            />
          )}
          {activeTab === 'normas' && <NtcTable ntcs={ntcs} nit={empresa.nit} />}
          {activeTab === 'indicadores' && <IndicadoresGrid nit={empresa.nit} />}
        </div>
      </div>

      {/* Modal: Editar Detalles de Empresa */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Editar Detalles de la Empresa" size="md">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSaveDetails();
          }}
          className="space-y-4"
        >
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Departamento"
              value={formValues.departamento}
              onChange={(e) => setFormValues((prev) => ({ ...prev, departamento: e.target.value }))}
              placeholder="Ej. Quindío"
            />
            <Input
              label="Municipio"
              value={formValues.municipio}
              onChange={(e) => setFormValues((prev) => ({ ...prev, municipio: e.target.value }))}
              placeholder="Ej. Armenia"
            />
          </div>

          <Input
            label="Ubicación / Dirección"
            value={formValues.ubicacion}
            onChange={(e) => setFormValues((prev) => ({ ...prev, ubicacion: e.target.value }))}
            placeholder="Ej. Km 5 Vía al Edén"
          />

          <Input
            label="Correo de la Empresa"
            type="email"
            value={formValues.correoEmpresa}
            onChange={(e) => setFormValues((prev) => ({ ...prev, correoEmpresa: e.target.value }))}
            placeholder="Ej. contacto@empresa.com"
          />

          <Input
            label="Representante Legal"
            value={formValues.representanteLegal}
            onChange={(e) => setFormValues((prev) => ({ ...prev, representanteLegal: e.target.value }))}
            placeholder="Nombre del representante"
          />

          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <Input
                label="RNT"
                value={formValues.rnt}
                onChange={(e) => setFormValues((prev) => ({ ...prev, rnt: e.target.value }))}
                placeholder="Número RNT"
              />
            </div>
            <div className="col-span-1">
              <Input
                label="Categoría RNT"
                value={formValues.rntCategoria}
                onChange={(e) => setFormValues((prev) => ({ ...prev, rntCategoria: e.target.value }))}
                placeholder="Ej. Alojamiento"
              />
            </div>
            <div className="col-span-1">
              <Input
                label="Subcategoría RNT"
                value={formValues.rntSubCategoria}
                onChange={(e) => setFormValues((prev) => ({ ...prev, rntSubCategoria: e.target.value }))}
                placeholder="Ej. Hotel"
              />
            </div>
          </div>

          {saveDetailsError && (
            <p className="text-[12px]" style={{ color: 'var(--admin-red)' }}>
              {saveDetailsError}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: 'var(--admin-border)' }}>
            <button
              type="button"
              onClick={() => setIsEditModalOpen(false)}
              disabled={isSavingDetails}
              className="admin-btn disabled:opacity-50"
            >
              Cancelar
            </button>
            <button type="submit" disabled={isSavingDetails} className="admin-btn primary disabled:opacity-50">
              {isSavingDetails ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
